import { isDefined, isNotDefined } from '@sgme/fp';
import { Button, Icon, Tooltip } from '@sgme/ui';
import { FormattedMessage, useIntl } from 'react-intl';

import { useOptionSelector } from '@/App/tiles/Option/OptionContext.ts';
import { BidAskInput } from '@/App/tiles/Option/strategy/fields/BidAskInput.tsx';
import { type OptionCurrency, selectOptionCurrency } from '@/store/option/selectors/optionSelectors.ts';
import type { LegsData, OptionLegProperties } from '@/store/option/state.ts';
import type { BidAsk } from '@/types/common.ts';

export function ComMargin({
  leg,
  legData,
  onChange,
}: {
  leg: OptionLegProperties;
  legData: LegsData | undefined;
  onChange: (markup: number | null) => void;
}) {
  const markupCurrency = useOptionSelector((option) => option.properties.markupCurrency);
  const optionCurrency = useOptionSelector(selectOptionCurrency);
  const markupFromQuote = getMarkupFromQuote(legData, markupCurrency, optionCurrency);
  const { formatNumber } = useIntl();

  function handleUnpin() {
    onChange(null);
  }

  function formatMarkup(value: number | null | undefined): string {
    if (isNotDefined(value)) {
      return '';
    }
    const formatted = formatNumber(value);
    return value >= 0 ? `+${formatted}` : formatted;
  }

  return (
    <div className="d-flex flex-grow-1 flex-center">
      <BidAskInput
        editMode="single"
        data-e2e="commercialMargin"
        side={leg.side}
        format={formatMarkup}
        color
        value={{
          bid: leg.markup ?? markupFromQuote?.bid,
          ask: leg.markup ?? markupFromQuote?.ask,
        }}
        suffix={markupCurrency}
        onChange={onChange}
      />
      {isDefined(leg.markup) && (
        <Tooltip contents={<FormattedMessage id="option.pin.tooltip.message" />}>
          <Button onClick={handleUnpin} data-e2e="pin-button" size="sm" purpose="flat" iconPosition="start">
            <Icon icon="push_pin" />
          </Button>
        </Tooltip>
      )}
    </div>
  );
}

function getMarkupFromQuote(
  legData: LegsData | undefined,
  markupCurrency: string | undefined,
  optionCurrency: OptionCurrency,
): BidAsk<number | null> | undefined {
  const marginData = legData?.marginData;
  if (isNotDefined(marginData) || isNotDefined(markupCurrency)) {
    return undefined;
  }

  if (markupCurrency === optionCurrency.currency2) {
    return {
      bid: marginData.bidMarkupCcy2,
      ask: marginData.askMarkupCcy2,
    };
  }

  return {
    bid: marginData.bidMarkupCcy1,
    ask: marginData.askMarkupCcy1,
  };
}
